// src/screens/MatchLobbyScreen.js
// ============================================================================
// MATCH LOBBY SCREEN
// 
// Displays everything a user needs to join/create their match:
// - Match round info (Round 1, Semi-Finals, Finals, etc.)
// - Opponent info (name, IGN(s) for all team members)
// - Lobby credentials (name, password)
// - Step-by-step instructions for their game
// - Etiquette guidelines
// - Copy buttons for easy credential sharing
//
// SUPPORTS:
// - Solo matches (1v1)
// - Team matches (2v2, 3v3, 6v6, etc.)
// - Multi-round brackets (R1 → Semi-Finals → Finals)
// - H2H quick matches
// ============================================================================

import React, { useState, useEffect, useCallback } from 'react';
import {
  View, Text, TouchableOpacity, StyleSheet, ScrollView,
  ActivityIndicator, Platform, Alert, Clipboard
} from 'react-native';
import { useIsFocused } from '@react-navigation/native';
import { getAuth } from 'firebase/auth';
import { doc, onSnapshot } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../firebaseConfig';
import { useError } from '../components/ErrorContext';
import { GAME_PROFILES } from '../constants/gameData';
import Footer from '../components/Footer';

// ============================================================================
// GAME TYPE ICONS
// ============================================================================
const GAME_TYPE_ICONS = {
  fighting: '🥊',
  sports: '🏆',
  shooter: '🎯',
  moba: '🧙',
  rts: '⚔️',
  racing: '🏎️',
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================
export default function MatchLobbyScreen({ route, navigation }) {
  const { matchId, eventId } = route.params;
  const { showError } = useError();
  const isFocused = useIsFocused();
  const functions = getFunctions();
  const auth = getAuth();
  const currentUserId = auth.currentUser?.uid;

  const [isLoading, setIsLoading] = useState(true);
  const [matchData, setMatchData] = useState(null);
  const [eventData, setEventData] = useState(null);
  const [isHost, setIsHost] = useState(false);
  const [opponentIGNs, setOpponentIGNs] = useState([]);
  const [copiedField, setCopiedField] = useState(null);
  const [showInstructions, setShowInstructions] = useState(true);
  const [showEtiquette, setShowEtiquette] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  // ============================================================================
  // DATA FETCHING
  // ============================================================================
  useEffect(() => {
    if (!isFocused || !matchId || !eventId) return;

    // Subscribe to match updates
    const matchRef = doc(db, 'tournaments', eventId, 'matches', matchId);
    const unsubMatch = onSnapshot(matchRef, (matchSnap) => {
      if (!matchSnap.exists()) {
        showError('Match not found');
        if (navigation.canGoBack()) navigation.goBack(); else navigation.navigate('Lobby');
        return;
      }

      const match = { id: matchSnap.id, ...matchSnap.data() };
      setMatchData(match);

      // Determine if current user is the HOST from the match doc (audit M3) —
      // the seeders stamp hostUserId/hostIsP1, and the host may be on either
      // side. Fall back to the legacy P1-is-host convention for old matches.
      const userIsP1 = isUserInParticipant(currentUserId, match.p1);
      const stampedHost = match.hostUserId || match.hostUid || null;
      const userIsHost = stampedHost
        ? currentUserId === stampedHost
        : (typeof match.hostIsP1 === 'boolean' ? (match.hostIsP1 ? userIsP1 : !userIsP1) : userIsP1);
      setIsHost(userIsHost);

      // Get opponent IGNs — the other SIDE from the caller (not host-relative)
      const igns = userIsP1 ? (match.p2IGNs || []) : (match.p1IGNs || []);
      setOpponentIGNs(igns);

      setIsLoading(false);
    }, (error) => {
      console.error('Match subscription error:', error);
      showError('Failed to load match data');
      setIsLoading(false);
    });

    // Subscribe to event updates
    const eventRef = doc(db, 'tournaments', eventId);
    const unsubEvent = onSnapshot(eventRef, (eventSnap) => {
      if (eventSnap.exists()) {
        setEventData({ id: eventSnap.id, ...eventSnap.data() });
      }
    });

    return () => {
      unsubMatch();
      unsubEvent();
    };
  }, [isFocused, matchId, eventId, currentUserId]);

  // ============================================================================
  // HELPERS
  // ============================================================================
  function isUserInParticipant(uid, participant) {
    if (!participant) return false;
    
    // Solo player
    const participantUid = participant.userId || participant.id || participant.odref?.id;
    if (participantUid === uid) return true;
    
    // Team member
    if (participant.members && Array.isArray(participant.members)) {
      return participant.members.some(m => (m.userId || m.id) === uid);
    }
    
    return false;
  }

  function getOpponentDisplayName() {
    // Opponent = the other SIDE from the caller (audit M3 — the host may be
    // on either side, so host-relative lookup showed the wrong name).
    const userIsP1 = isUserInParticipant(currentUserId, matchData?.p1);
    const opponent = userIsP1 ? matchData?.p2 : matchData?.p1;
    if (!opponent) return 'Opponent';
    if (opponent.isBye) return 'BYE';
    if (opponent.teamName) return opponent.teamName;
    return opponent.visibleName || 'Opponent';
  }

  function getRoundName(round, totalRounds) {
    if (!round || !totalRounds) return 'Match';
    if (round === totalRounds) return 'Finals';
    if (round === totalRounds - 1) return 'Semi-Finals';
    if (round === totalRounds - 2) return 'Quarter-Finals';
    return `Round ${round}`;
  }

  // ============================================================================
  // CLIPBOARD HANDLERS
  // ============================================================================
  const copyToClipboard = useCallback((text, fieldName) => {
    if (Platform.OS === 'web') {
      navigator.clipboard.writeText(text);
    } else {
      Clipboard.setString(text);
    }
    setCopiedField(fieldName);
    setTimeout(() => setCopiedField(null), 2000);
  }, []);

  // ============================================================================
  // STATUS UPDATE HANDLERS
  // ============================================================================
  const handleStatusUpdate = async (status) => {
    setIsUpdatingStatus(true);
    try {
      const updateStatus = httpsCallable(functions, 'updateMatchLobbyStatus');
      await updateStatus({ matchId, eventId, status });
      
      const message = status === 'lobby_created' 
        ? 'Lobby created! Waiting for opponent to join.'
        : 'You\'ve joined the lobby!';
      Alert.alert('Success', message);
    } catch (error) {
      console.error('Status update error:', error);
      showError(error.message || 'Failed to update status');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  // ============================================================================
  // RENDER HELPERS
  // ============================================================================
  const renderCredentialCard = (label, value, fieldName, icon) => (
    <View style={styles.credentialCard}>
      <View style={styles.credentialHeader}>
        <Text style={styles.credentialIcon}>{icon}</Text>
        <Text style={styles.credentialLabel}>{label}</Text>
      </View>
      <View style={styles.credentialValueRow}>
        <Text style={styles.credentialValue}>{value || '---'}</Text>
        <TouchableOpacity
          style={[styles.copyButton, copiedField === fieldName && styles.copyButtonSuccess]}
          onPress={() => value && copyToClipboard(value, fieldName)}
          disabled={!value}
        >
          <Text style={[styles.copyButtonText, copiedField === fieldName && styles.copyButtonTextSuccess]}>
            {copiedField === fieldName ? '✓ Copied' : 'Copy'}
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  );

  const renderOpponentIGN = (ignData, index) => (
    <View key={index} style={styles.ignCard}>
      <View style={styles.ignInfo}>
        <Text style={styles.ignPlayerName}>{ignData.visibleName || `Player ${index + 1}`}</Text>
        <Text style={styles.ignValue}>{ignData.ign || 'IGN not set'}</Text>
      </View>
      {ignData.ign && (
        <TouchableOpacity
          style={styles.ignCopyButton}
          onPress={() => copyToClipboard(ignData.ign, `ign-${index}`)}
        >
          <Text style={styles.ignCopyText}>
            {copiedField === `ign-${index}` ? '✓' : '📋'}
          </Text>
        </TouchableOpacity>
      )}
    </View>
  );

  const renderInstructionStep = (step, index) => {
    if (step.startsWith('  •')) {
      return (
        <Text key={index} style={styles.instructionSubStep}>{step.trim()}</Text>
      );
    }
    
    const parts = step.split(/\*\*(.*?)\*\*/g);
    
    return (
      <View key={index} style={styles.instructionStepRow}>
        <Text style={styles.instructionStepNumber}>{index + 1}</Text>
        <Text style={styles.instructionStepText}>
          {parts.map((part, i) => 
            i % 2 === 1 ? <Text key={i} style={styles.boldText}>{part}</Text> : part
          )}
        </Text>
      </View>
    );
  };

  // ============================================================================
  // LOADING STATE
  // ============================================================================
  if (isLoading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#FCD000" />
        <Text style={styles.loadingText}>Loading match details...</Text>
      </View>
    );
  }

  // ============================================================================
  // MAIN RENDER
  // ============================================================================
  const gameId = eventData?.game;
  const gameProfile = GAME_PROFILES[gameId] || {};
  const gameIcon = GAME_TYPE_ICONS[gameProfile.type] || '🎮';
  const lobbyInfo = matchData?.lobbyInfo || {};
  // Madden ('mdn') is a direct EA-IGN invite — no lobby name/password.
  const isMaddenInvite = matchData?.inviteMode === 'ea_ign' || String(gameId || '').toLowerCase() === 'mdn';
  const lobbyReady = matchData?.lobbyReady || {};
  const roundName = getRoundName(matchData?.round, eventData?.totalRounds);
  const isTeamGame = (eventData?.teamSize || 1) > 1;
  
  // Get instructions
  const instructions = getInstructionsForGame(gameId, isHost, {
    lobbyName: lobbyInfo.lobbyName,
    password: lobbyInfo.password,
    roomId: matchData?.roomId,
    format: eventData?.format,
    teamSize: eventData?.teamSize || 1,
  });

  return (
    <View style={styles.container}>
      <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollContent}>
        
        {/* Match Header */}
        <View style={styles.header}>
          <View style={styles.roundBadge}>
            <Text style={styles.roundBadgeText}>{roundName}</Text>
          </View>
          <View style={styles.gameInfo}>
            <Text style={styles.gameIcon}>{gameIcon}</Text>
            <View>
              <Text style={styles.gameName}>{gameProfile.displayName || 'Game'}</Text>
              <Text style={styles.formatText}>{eventData?.format || 'Match'}</Text>
            </View>
          </View>
          <View style={[styles.roleBadge, isHost ? styles.hostBadge : styles.playerBadge]}>
            <Text style={styles.roleBadgeText}>{isHost ? '👑 HOST' : '🎮 PLAYER'}</Text>
          </View>
        </View>

        {/* Lobby Status Banner */}
        {matchData?.lobbyStatus && (
          <View style={[
            styles.statusBanner,
            matchData.lobbyStatus === 'both_ready' && styles.statusBannerReady
          ]}>
            <Text style={styles.statusBannerText}>
              {matchData.lobbyStatus === 'credentials_generated' && '⏳ Waiting for lobby setup...'}
              {matchData.lobbyStatus === 'awaiting_room_id' && '⏳ Waiting for host to submit GT7 Room ID...'}
              {matchData.lobbyStatus === 'room_id_set' && '🏁 Room ID ready — join the lobby in GT7!'}
              {matchData.lobbyStatus === 'lobby_created' && '✅ Host created lobby - Player joining...'}
              {matchData.lobbyStatus === 'player_joined' && '✅ Player joined - Waiting for host...'}
              {matchData.lobbyStatus === 'both_ready' && '🎮 Both players ready - START YOUR MATCH!'}
              {matchData.lobbyStatus === 'credentials_regenerated' && '🔄 New credentials generated'}
            </Text>
          </View>
        )}

        {/* Racers Section — heats (players[] FFA, no p1/p2, incl. 2-racer
            finals of tiny events): the pairwise opponent card would render
            "Opponent / IGN not set" */}
        {Array.isArray(matchData?.players) && matchData.players.length >= 2 && !matchData?.p1 ? (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>
              Racers ({matchData.players.length})
            </Text>
            <View style={styles.ignList}>
              <Text style={styles.ignListTitle}>Your heat — look for these racers:</Text>
              {matchData.players.map((p) => (
                <Text key={p.userId} style={styles.opponentName}>
                  {p.displayName || 'Racer'}
                  {p.ign ? <Text style={styles.ignHint}>  ({p.ign})</Text> : null}
                  {(matchData.hostUserId || matchData.hostUid) === p.userId
                    ? <Text style={{ color: '#FCD000', fontSize: 12 }}>  👑 HOST</Text> : null}
                </Text>
              ))}
            </View>
            <Text style={styles.ignHint}>
              {isHost
                ? 'Create the private room and share the Room ID — the racers above join your lobby.'
                : 'Join the heat lobby with the Room ID once the host opens it.'}
            </Text>
          </View>
        ) : (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>
            {isTeamGame ? 'Opponent Team' : 'Your Opponent'}
          </Text>

          {/* Opponent Name */}
          <View style={styles.opponentHeader}>
            <View style={styles.opponentAvatar}>
              <Text style={styles.opponentAvatarText}>
                {getOpponentDisplayName()[0]?.toUpperCase() || '?'}
              </Text>
            </View>
            <Text style={styles.opponentName}>{getOpponentDisplayName()}</Text>
          </View>

          {/* Opponent IGN(s) */}
          <View style={styles.ignList}>
            <Text style={styles.ignListTitle}>
              {isTeamGame ? 'Team Members - Look for these players:' : 'In-Game Name - Look for:'}
            </Text>
            {opponentIGNs.length > 0 ? (
              opponentIGNs.map((ign, index) => renderOpponentIGN(ign, index))
            ) : (
              <Text style={styles.noIgnText}>IGN not set - coordinate via chat</Text>
            )}
          </View>

          <Text style={styles.ignHint}>
            {isHost
              ? 'Invite these players to your lobby or wait for them to join'
              : 'Search for this lobby or accept invite from host'}
          </Text>
        </View>
        )}

        {/* Lobby Credentials */}
        {String(gameId || '').toLowerCase() === 'gt7' ? (
          // GT7 (post-Update 1.65): GT7 auto-generates a 6-char Room ID when the
          // host creates a Private room — no name or password to set.
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>GT7 Room ID</Text>
            {matchData?.roomId ? (
              <>
                <View style={styles.credentialsContainer}>
                  {renderCredentialCard('Room ID', matchData.roomId, 'roomId', '🏁')}
                </View>
                <Text style={styles.credentialHint}>
                  {isHost
                    ? 'This is the Room ID GT7 assigned to your lobby — racers use it to join.'
                    : 'Open GT7 → Lobby → Enter Room ID → type the code above to join the race.'}
                </Text>
              </>
            ) : (
              <View style={styles.credentialsContainer}>
                <Text style={styles.credentialHint}>
                  {isHost
                    ? 'You\'ll be prompted to submit the Room ID once you create the private room in GT7.'
                    : 'Waiting for the host to create the GT7 lobby and share the Room ID...'}
                </Text>
              </View>
            )}
          </View>
        ) : isMaddenInvite ? (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>EA Invite</Text>
            <View style={styles.credentialsContainer}>
              <Text style={styles.credentialHint}>
                Madden uses a direct EA invite — there's no lobby name or password. Check your match notification or email for who to invite (or who invites you), then send/accept the invite by EA IGN.
              </Text>
            </View>
            {matchData?.crossplayEnabled && (
              <Text style={[styles.credentialHint, { marginTop: 8, color: '#FCD000' }]}>
                ⚠️ Crossplay is ON — make sure crossplay is enabled (PS5/PC) before sending/accepting the invite.
              </Text>
            )}
          </View>
        ) : (
          <View style={styles.section}>
            <Text style={styles.sectionTitle}>Lobby Credentials</Text>
            <View style={styles.credentialsContainer}>
              {renderCredentialCard('Lobby Name', lobbyInfo.lobbyName, 'lobbyName', '🏷️')}
              {renderCredentialCard('Password', lobbyInfo.password, 'password', '🔑')}
            </View>
            <Text style={styles.credentialHint}>
              {isHost
                ? 'Use these exactly when creating your private match'
                : 'Use these to find and join the host\'s lobby'}
            </Text>
          </View>
        )}

        {/* Instructions Accordion */}
        <View style={styles.section}>
          <TouchableOpacity 
            style={styles.accordionHeader}
            onPress={() => setShowInstructions(!showInstructions)}
          >
            <Text style={styles.sectionTitle}>
              {isMaddenInvite
                ? '📝 How the EA Invite Works'
                : (isHost ? '📝 How to Create the Lobby' : '📝 How to Join the Lobby')}
            </Text>
            <Text style={styles.accordionIcon}>{showInstructions ? '▼' : '▶'}</Text>
          </TouchableOpacity>

          {showInstructions && (
            <View style={styles.instructionsContainer}>
              {isMaddenInvite ? (
                <>
                  {renderInstructionStep('Madden uses a direct EA invite — no lobby code.', 0)}
                  {renderInstructionStep('Check your match notification/email for who to invite (or who invites you), by EA IGN.', 1)}
                  {renderInstructionStep('Send or accept the EA invite in Madden.', 2)}
                  {renderInstructionStep('Make sure your stream is live with the CritCash title, then start the match.', 3)}
                </>
              ) : (
                <>
                  {instructions.steps.map((step, index) => renderInstructionStep(step, index))}

                  {instructions.settingsReminder?.length > 0 && (
                    <View style={styles.settingsReminder}>
                      <Text style={styles.settingsReminderTitle}>⚙️ Settings Checklist</Text>
                      {instructions.settingsReminder.map((setting, index) => (
                        <Text key={index} style={styles.settingsReminderItem}>• {setting}</Text>
                      ))}
                    </View>
                  )}
                </>
              )}
            </View>
          )}
        </View>

        {/* Etiquette Section */}
        <View style={styles.section}>
          <TouchableOpacity 
            style={styles.accordionHeader}
            onPress={() => setShowEtiquette(!showEtiquette)}
          >
            <Text style={styles.sectionTitle}>🤝 Match Etiquette</Text>
            <Text style={styles.accordionIcon}>{showEtiquette ? '▼' : '▶'}</Text>
          </TouchableOpacity>
          
          {showEtiquette && (
            <View style={styles.etiquetteContainer}>
              {/* Key Points */}
              <View style={styles.keyPointCard}>
                <Text style={styles.keyPointIcon}>🔇</Text>
                <View style={styles.keyPointContent}>
                  <Text style={styles.keyPointTitle}>Keep Mics Off</Text>
                  <Text style={styles.keyPointText}>
                    Best practice is to mute voice chat. Things can get heated in a competitive match - stay professional.
                  </Text>
                </View>
              </View>

              <View style={styles.keyPointCard}>
                <Text style={styles.keyPointIcon}>💰</Text>
                <View style={styles.keyPointContent}>
                  <Text style={styles.keyPointTitle}>Your Record on the Line</Text>
                  <Text style={styles.keyPointText}>
                    Real competition means real professionalism. Compete hard, but play fair and respectfully.
                  </Text>
                </View>
              </View>

              {/* Game-specific etiquette */}
              {instructions.etiquette?.map((rule, index) => (
                <Text key={index} style={styles.etiquetteItem}>{rule}</Text>
              ))}

              {/* General rules */}
              <View style={styles.generalRules}>
                <Text style={styles.generalRulesTitle}>Quick Rules</Text>
                <Text style={styles.generalRulesItem}>✅ Join within 5 minutes of notification</Text>
                <Text style={styles.generalRulesItem}>✅ Play fair - no exploits or glitches</Text>
                <Text style={styles.generalRulesItem}>✅ Say "GG" at the end - win or lose</Text>
                <Text style={styles.generalRulesItem}>🚫 No rage-quitting or disconnecting</Text>
                <Text style={styles.generalRulesItem}>🚫 No toxic messages or harassment</Text>
              </View>
            </View>
          )}
        </View>

        {/* Action Buttons */}
        <View style={styles.actionsSection}>
          {isHost ? (
            <View style={styles.hostActions}>
              <Text style={styles.actionInstructionText}>
                Create the lobby using the credentials above, then confirm when ready.
              </Text>
              <TouchableOpacity 
                style={[
                  styles.primaryButton,
                  lobbyReady.host && styles.primaryButtonDone,
                  isUpdatingStatus && styles.primaryButtonDisabled
                ]}
                onPress={() => handleStatusUpdate('lobby_created')}
                disabled={lobbyReady.host || isUpdatingStatus}
              >
                {isUpdatingStatus ? (
                  <ActivityIndicator size="small" color="#000" />
                ) : (
                  <Text style={styles.primaryButtonText}>
                    {lobbyReady.host ? '✓ Lobby Created' : 'I\'ve Created the Lobby'}
                  </Text>
                )}
              </TouchableOpacity>
            </View>
          ) : (
            <View style={styles.playerActions}>
              <Text style={styles.actionInstructionText}>
                Join the lobby using the credentials above, then confirm when you're in.
              </Text>
              <TouchableOpacity 
                style={[
                  styles.primaryButton,
                  lobbyReady.player && styles.primaryButtonDone,
                  isUpdatingStatus && styles.primaryButtonDisabled
                ]}
                onPress={() => handleStatusUpdate('player_joined')}
                disabled={lobbyReady.player || isUpdatingStatus}
              >
                {isUpdatingStatus ? (
                  <ActivityIndicator size="small" color="#000" />
                ) : (
                  <Text style={styles.primaryButtonText}>
                    {lobbyReady.player ? '✓ Joined Lobby' : 'I\'ve Joined the Lobby'}
                  </Text>
                )}
              </TouchableOpacity>
            </View>
          )}
          
          <TouchableOpacity 
            style={styles.secondaryButton}
            onPress={() => navigation.navigate('ReportIssue', { matchId, eventId })}
          >
            <Text style={styles.secondaryButtonText}>Report an Issue</Text>
          </TouchableOpacity>
        </View>

        <View style={{ height: 40 }} />
      </ScrollView>
      
      <Footer />
    </View>
  );
}

// ============================================================================
// GAME-SPECIFIC INSTRUCTIONS
// ============================================================================
function getInstructionsForGame(gameId, isHost, details) {
  const { lobbyName, password, roomId, format, teamSize } = details;
  
  const INSTRUCTIONS = {
    'rkl': {
      host: {
        steps: [
          'From the main menu, select **Play** → **Private Match** → **Create Private Match**',
          `Set the **Match Name** to: **${lobbyName || 'CC-XXXX'}**`,
          `Set the **Password** to: **${password || 'xxxx00'}**`,
          'Configure match settings:',
          `  • Team Size: ${teamSize || 1}v${teamSize || 1}`,
          '  • Bot Difficulty: No Bots',
          '  • Joinable By: Name/Password',
          'Select your arena and click **Create**',
          'Wait for your opponent to join before starting',
        ],
        settingsReminder: [
          'Mutators: None (default settings)',
          'Time limit: 5 minutes',
          'Overtime: Unlimited',
        ],
      },
      player: {
        steps: [
          'From the main menu, select **Play** → **Private Match** → **Join Private Match**',
          `Enter the Match Name: **${lobbyName || 'CC-XXXX'}**`,
          `Enter the Password: **${password || 'xxxx00'}**`,
          'Click **Search** and join the match',
          'Select your team and ready up',
        ],
        settingsReminder: [],
      },
      etiquette: [
        '⏱️ Be Ready: Join within 5 minutes of notification',
        '🤝 No toxic quick chats - keep it clean',
        '📵 Don\'t intentionally stall or delay',
      ],
    },
    'sf6': {
      host: {
        steps: [
          'Go to **Battle Hub** or **Fighting Ground** → **Custom Room**',
          'Select **Create Room**',
          `Set Room Name to: **${lobbyName || 'CC-XXXX'}**`,
          `Set Password to: **${password || 'xxxx00'}**`,
          'Configure room settings:',
          '  • Room Type: Private',
          '  • Max Players: 2',
          `  • Rounds to Win: ${format || 'Best of 3'}`,
          'Create the room and wait for your opponent',
        ],
        settingsReminder: [
          'Round Timer: 99 seconds',
          'No handicap settings',
        ],
      },
      player: {
        steps: [
          'Go to **Battle Hub** or **Fighting Ground** → **Custom Room**',
          'Select **Search** or **Join Room**',
          `Search for Room ID: **${lobbyName || 'CC-XXXX'}**`,
          `Enter Password: **${password || 'xxxx00'}**`,
          'Join and ready up at the cabinet',
        ],
        settingsReminder: [],
      },
      etiquette: [
        '🎮 Pick your character promptly',
        '⏸️ No pausing mid-match',
        '🤝 Play out the full set - no rage quitting',
      ],
    },
    'tk8': {
      host: {
        steps: [
          'From main menu, go to **Online** → **Lobby Match** → **Create Lobby**',
          `Set Lobby Name to: **${lobbyName || 'CC-XXXX'}**`,
          `Set Password to: **${password || 'xxxx00'}**`,
          'Configure settings:',
          '  • Max Players: 2',
          `  • Rounds: ${format || 'Best of 3'}`,
          '  • Private Slots: 2',
          'Create and wait for opponent',
        ],
        settingsReminder: [
          'Round time: 60 seconds',
          'Stage: Random or mutual agreement',
        ],
      },
      player: {
        steps: [
          'Go to **Online** → **Lobby Match** → **Search Lobby**',
          `Search for lobby: **${lobbyName || 'CC-XXXX'}**`,
          `Enter password: **${password || 'xxxx00'}**`,
          'Join and select your character',
        ],
        settingsReminder: [],
      },
      etiquette: [
        '🎭 Lock in character before starting',
        '🚫 Disconnecting = forfeit',
        '🤝 GGs after every set',
      ],
    },
    // GT7 (post-Update 1.65 / Spec III, Dec 2025): hosts can't name lobbies
    // or set passwords. GT7 auto-generates a 6-char Room ID; host enters it
    // into CritCash via the HostRoomIdModal which then fans out to racers.
    'gt7': {
      host: {
        steps: [
          'From the main menu, go to **Multiplayer** → **Lobby** → **Create New Room**',
          'Set **Room Privacy** to **Private** (so only racers with the Room ID can join)',
          `Apply the configured race settings — track, laps, car category, pit/tire rules`,
          'GT7 will display a **Room ID** in the top right (e.g. souij4)',
          'Submit that Room ID in the CritCash app when prompted',
          'Wait at the lobby screen for racers to join, then start the practice/qualifier/race',
        ],
        settingsReminder: [
          'Privacy: Private — racers join via Room ID, not the lobby browser',
          `Max participants: 16 (you occupy one slot, 15 racers join)`,
          'Use the exact race settings shown for this event',
        ],
      },
      player: {
        steps: [
          'Wait for the host to share the Room ID',
          'Open GT7 → **Multiplayer** → **Lobby**',
          'Tap **Enter Room ID** at the top of the lobby browser',
          roomId
            ? `Type **${roomId}** and press OK`
            : 'Type the 6-character Room ID and press OK',
          'Pick a car (event car category) and ready up on the grid',
        ],
        settingsReminder: [],
      },
      etiquette: [
        '🏁 Be ready on the grid before the host starts the race',
        '🚫 No intentional ramming — penalties are enforced',
        '🎤 Keep voice chat respectful',
      ],
    },
  };

  const gameInstructions = INSTRUCTIONS[gameId];
  
  if (!gameInstructions) {
    return {
      steps: isHost ? [
        'Create a private/custom match in your game',
        `Set Lobby Name to: **${lobbyName || 'CC-XXXX'}**`,
        `Set Password to: **${password || 'xxxx00'}**`,
        'Configure match settings per event rules',
        'Wait for opponent to join',
      ] : [
        'Navigate to join/search in your game',
        `Search for lobby: **${lobbyName || 'CC-XXXX'}**`,
        `Enter password: **${password || 'xxxx00'}**`,
        'Join and ready up',
      ],
      settingsReminder: isHost ? ['Follow event rules'] : [],
      etiquette: [
        '🔇 Keep voice chat muted',
        '🎮 Play fair - no exploits',
        '🤝 Be respectful',
      ],
    };
  }

  const instructions = isHost ? gameInstructions.host : gameInstructions.player;
  return {
    ...instructions,
    etiquette: gameInstructions.etiquette,
  };
}

// ============================================================================
// STYLES
// ============================================================================
const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#0D0D12',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    padding: 16,
  },

  // Loading
  loadingContainer: {
    flex: 1,
    backgroundColor: '#0D0D12',
    justifyContent: 'center',
    alignItems: 'center',
  },
  loadingText: {
    color: '#888',
    marginTop: 12,
    fontSize: 14,
  },

  // Header
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#2A2A36',
  },
  roundBadge: {
    position: 'absolute',
    top: -8,
    left: 0,
    right: 0,
    alignItems: 'center',
  },
  roundBadgeText: {
    backgroundColor: '#FCD000',
    color: '#000',
    fontSize: 12,
    fontWeight: '800',
    paddingHorizontal: 16,
    paddingVertical: 4,
    borderRadius: 12,
    overflow: 'hidden',
  },
  gameInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    marginTop: 20,
  },
  gameIcon: {
    fontSize: 32,
    marginRight: 10,
  },
  gameName: {
    fontSize: 18,
    fontWeight: '800',
    color: '#FFF',
  },
  formatText: {
    fontSize: 13,
    color: '#888',
  },
  roleBadge: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 20,
    marginTop: 20,
  },
  hostBadge: {
    backgroundColor: '#FCD000',
  },
  playerBadge: {
    backgroundColor: '#4A90D9',
  },
  roleBadgeText: {
    fontSize: 11,
    fontWeight: '700',
    color: '#000',
  },

  // Status Banner
  statusBanner: {
    backgroundColor: '#1A2B4A',
    borderRadius: 10,
    padding: 12,
    marginBottom: 16,
    alignItems: 'center',
  },
  statusBannerReady: {
    backgroundColor: '#1A4A2B',
  },
  statusBannerText: {
    color: '#8AB4F8',
    fontSize: 14,
    fontWeight: '600',
  },

  // Sections
  section: {
    marginBottom: 20,
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: '#FFF',
    marginBottom: 10,
  },

  // Opponent
  opponentHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 12,
  },
  opponentAvatar: {
    width: 48,
    height: 48,
    borderRadius: 24,
    backgroundColor: '#FCD000',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 12,
  },
  opponentAvatarText: {
    fontSize: 20,
    fontWeight: '800',
    color: '#000',
  },
  opponentName: {
    fontSize: 18,
    fontWeight: '700',
    color: '#FFF',
  },

  // IGN List
  ignList: {
    backgroundColor: '#1A1A24',
    borderRadius: 12,
    padding: 14,
    borderWidth: 1,
    borderColor: '#2A2A36',
  },
  ignListTitle: {
    fontSize: 13,
    color: '#888',
    marginBottom: 10,
  },
  ignCard: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#0D0D12',
    borderRadius: 8,
    padding: 10,
    marginBottom: 6,
  },
  ignInfo: {
    flex: 1,
  },
  ignPlayerName: {
    fontSize: 12,
    color: '#888',
  },
  ignValue: {
    fontSize: 16,
    fontWeight: '700',
    color: '#FCD000',
  },
  ignCopyButton: {
    padding: 8,
  },
  ignCopyText: {
    fontSize: 16,
  },
  noIgnText: {
    fontSize: 14,
    color: '#666',
    fontStyle: 'italic',
  },
  ignHint: {
    fontSize: 12,
    color: '#666',
    marginTop: 8,
    fontStyle: 'italic',
  },

  // Credentials
  credentialsContainer: {
    gap: 10,
  },
  credentialCard: {
    backgroundColor: '#1A1A24',
    borderRadius: 12,
    padding: 12,
    borderWidth: 1,
    borderColor: '#2A2A36',
  },
  credentialHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 6,
  },
  credentialIcon: {
    fontSize: 14,
    marginRight: 6,
  },
  credentialLabel: {
    fontSize: 12,
    color: '#888',
  },
  credentialValueRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  credentialValue: {
    fontSize: 20,
    fontWeight: '800',
    color: '#FCD000',
    fontFamily: Platform.OS === 'ios' ? 'Menlo' : 'monospace',
    letterSpacing: 1,
  },
  copyButton: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    backgroundColor: '#2A2A36',
    borderRadius: 6,
  },
  copyButtonSuccess: {
    backgroundColor: '#2D4A2D',
  },
  copyButtonText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#FFF',
  },
  copyButtonTextSuccess: {
    color: '#4CAF50',
  },
  credentialHint: {
    fontSize: 11,
    color: '#666',
    marginTop: 8,
    fontStyle: 'italic',
  },

  // Accordion
  accordionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  accordionIcon: {
    fontSize: 11,
    color: '#888',
  },

  // Instructions
  instructionsContainer: {
    backgroundColor: '#1A1A24',
    borderRadius: 12,
    padding: 14,
    marginTop: 8,
    borderWidth: 1,
    borderColor: '#2A2A36',
  },
  instructionStepRow: {
    flexDirection: 'row',
    marginBottom: 12,
  },
  instructionStepNumber: {
    width: 22,
    height: 22,
    borderRadius: 11,
    backgroundColor: '#FCD000',
    color: '#000',
    fontSize: 12,
    fontWeight: '700',
    textAlign: 'center',
    lineHeight: 22,
    marginRight: 10,
  },
  instructionStepText: {
    flex: 1,
    fontSize: 13,
    color: '#CCC',
    lineHeight: 20,
  },
  instructionSubStep: {
    fontSize: 12,
    color: '#999',
    marginLeft: 32,
    marginBottom: 4,
  },
  boldText: {
    fontWeight: '700',
    color: '#FFF',
  },
  settingsReminder: {
    marginTop: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#2A2A36',
  },
  settingsReminderTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: '#FFF',
    marginBottom: 8,
  },
  settingsReminderItem: {
    fontSize: 12,
    color: '#888',
    marginBottom: 4,
  },

  // Etiquette
  etiquetteContainer: {
    backgroundColor: '#1A1A24',
    borderRadius: 12,
    padding: 14,
    marginTop: 8,
    borderWidth: 1,
    borderColor: '#2A2A36',
  },
  keyPointCard: {
    flexDirection: 'row',
    backgroundColor: '#0D0D12',
    borderRadius: 8,
    padding: 12,
    marginBottom: 10,
  },
  keyPointIcon: {
    fontSize: 20,
    marginRight: 10,
  },
  keyPointContent: {
    flex: 1,
  },
  keyPointTitle: {
    fontSize: 14,
    fontWeight: '700',
    color: '#FCD000',
    marginBottom: 2,
  },
  keyPointText: {
    fontSize: 12,
    color: '#AAA',
    lineHeight: 16,
  },
  etiquetteItem: {
    fontSize: 13,
    color: '#CCC',
    marginBottom: 8,
  },
  generalRules: {
    marginTop: 8,
    paddingTop: 8,
    borderTopWidth: 1,
    borderTopColor: '#2A2A36',
  },
  generalRulesTitle: {
    fontSize: 13,
    fontWeight: '600',
    color: '#FFF',
    marginBottom: 8,
  },
  generalRulesItem: {
    fontSize: 12,
    color: '#888',
    marginBottom: 6,
  },

  // Actions
  actionsSection: {
    marginTop: 8,
  },
  hostActions: {},
  playerActions: {},
  actionInstructionText: {
    fontSize: 13,
    color: '#888',
    marginBottom: 10,
    textAlign: 'center',
  },
  primaryButton: {
    backgroundColor: '#FCD000',
    paddingVertical: 14,
    borderRadius: 10,
    alignItems: 'center',
  },
  primaryButtonDone: {
    backgroundColor: '#2D4A2D',
  },
  primaryButtonDisabled: {
    opacity: 0.6,
  },
  primaryButtonText: {
    fontSize: 15,
    fontWeight: '700',
    color: '#000',
  },
  secondaryButton: {
    paddingVertical: 12,
    borderRadius: 10,
    alignItems: 'center',
    borderWidth: 1,
    borderColor: '#2A2A36',
    marginTop: 10,
  },
  secondaryButtonText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#888',
  },
});